// @flow

/**
 * This implementation is based on http://www.perry.cz/files/ExifRestorer.js
 * It parses the dataUrl of original image, copies the segment that contains exif data, and writes it back to the cropped image.
 */
const KEY_STR = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';

/**
 * encode64 and decode64 methods here are not same as atob/btoa.
 * decode64 method will convert the dataUrl string into an int[], using the index of each character of dataUrl in KEY_STR.
 * encode64 will convert this int[] back into a string.
 */

/* eslint-disable no-bitwise */
const encode64 = input => {
  let output = '';
  let chr1 = '';
  let chr2 = '';
  let chr3 = '';
  let enc1 = '';
  let enc2 = '';
  let enc3 = '';
  let enc4 = '';
  let i = 0;

  do {
    chr1 = input[i++];
    chr2 = input[i++];
    chr3 = input[i++];

    enc1 = chr1 >> 2;
    enc2 = ((chr1 & 3) << 4) | (chr2 >> 4);
    enc3 = ((chr2 & 15) << 2) | (chr3 >> 6);
    enc4 = chr3 & 63;

    if (isNaN(chr2)) {
      enc3 = 64;
      enc4 = 64;
    } else if (isNaN(chr3)) {
      enc4 = 64;
    }

    output = `${output}${KEY_STR.charAt(enc1)}${KEY_STR.charAt(enc2)}${KEY_STR.charAt(
      enc3,
    )}${KEY_STR.charAt(enc4)}`;
    chr1 = '';
    chr2 = '';
    chr3 = '';
    enc1 = '';
    enc2 = '';
    enc3 = '';
    enc4 = '';
  } while (i < input.length);

  return output;
};

const decode64 = input => {
  let chr1 = '';
  let chr2 = '';
  let chr3 = '';
  let enc1 = '';
  let enc2 = '';
  let enc3 = '';
  let enc4 = '';
  let i = 0;
  const buf = [];

  do {
    enc1 = KEY_STR.indexOf(input.charAt(i++));
    enc2 = KEY_STR.indexOf(input.charAt(i++));
    enc3 = KEY_STR.indexOf(input.charAt(i++));
    enc4 = KEY_STR.indexOf(input.charAt(i++));

    chr1 = (enc1 << 2) | (enc2 >> 4);
    chr2 = ((enc2 & 15) << 4) | (enc3 >> 2);
    chr3 = ((enc3 & 3) << 6) | enc4;

    buf.push(chr1);

    if (enc3 !== 64) {
      buf.push(chr2);
    }
    if (enc4 !== 64) {
      buf.push(chr3);
    }

    chr1 = '';
    chr2 = '';
    chr3 = '';
    enc1 = '';
    enc2 = '';
    enc3 = '';
    enc4 = '';
  } while (i < input.length);

  return buf;
};
/* eslint-enable no-bitwise */

/**
 * From the decoded image data find the portion that contains exif info
 */
const getExifArray = segments => {
  let seg;
  for (let x = 0; x < segments.length; x++) {
    seg = segments[x];
    if (seg[0] === 255 && seg[1] === 225) {
      return seg;
    }
  }
  return [];
};

/**
 * Decode the cropped image into an int[] and add the exif data obtained from the original image
 */
const insertExif = (imageData, exifArray) => {
  const buf = decode64(imageData);
  const separatePoint = buf.indexOf(255, 3);
  const mae = buf.slice(0, separatePoint);
  const ato = buf.slice(separatePoint);
  let array = mae;

  array = array.concat(exifArray);
  array = array.concat(ato);
  return array;
};

/**
 * Convert the decoded image into segments. One of these segments contains the exif data.
 */
const slice2Segments = rawImageArray => {
  let head = 0;
  const segments = [];

  do {
    if (rawImageArray[head] === 255 && rawImageArray[head + 1] === 218) {
      break;
    }
    if (rawImageArray[head] === 255 && rawImageArray[head + 1] === 216) {
      head += 2;
    } else {
      const length = rawImageArray[head + 2] * 256 + rawImageArray[head + 3];
      const endPoint = head + length + 2;
      const seg = rawImageArray.slice(head, endPoint);
      segments.push(seg);
      head = endPoint;
    }
  } while (head <= rawImageArray.length);

  return segments;
};

/**
 * Using the segments of the original image, find the part that contains exif data and insert it into the cropped image.
 */
const exifManipulation = (resizedFileBase64, segments) => {
  const exifArray = getExifArray(segments);
  const newImageArray = insertExif(resizedFileBase64, exifArray);
  return new Uint8Array(newImageArray);
};

/**
 * 1. remove the "data:image/jpeg;base64," header from the original image so we have the image data
 * 2. decode the image data into int[]
 * 3. from the decoded int[] find the portion that contains exif data
 * 4. remove the "data:image/jpeg;base64," header from the cropped image
 * 5. decode the cropped image data into int[] and add exif data obtained in step3
 * 6. encode the manipulated int[] in step 5
 * 7. add "data:image/jpeg;base64," header back to encoded string and return
 *
 * orig file and resized file should have same mimetype otherwise adding exif will break the target photo.
 */
const restoreExif = (origFileBase64: string, resizedFileBase64: string, mimetype: string) => {
  const header = `data:${mimetype};base64,`;
  if (!origFileBase64.match(header)) {
    return resizedFileBase64;
  }

  const origFileWithoutHeader = origFileBase64.replace(header, '');

  const base64test = /[^A-Za-z0-9+/=]/g;
  if (base64test.exec(origFileWithoutHeader)) {
    return resizedFileBase64;
  }

  const rawImage = decode64(origFileWithoutHeader);
  const segments = slice2Segments(rawImage);

  const resizedFileWithoutHeader = resizedFileBase64.replace(header, '');
  const image = exifManipulation(resizedFileWithoutHeader, segments);
  return header + encode64(image);
};

export { restoreExif as default };
